import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, startCheckout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WebOnlyPurchase } from "@/components/WebOnlyPurchase";
import { PayByInvoiceLink } from "@/components/PayByInvoice";
import { Price } from "@/components/Price";
import RedeemAccessCode from "@/components/RedeemAccessCode";
import { Switch } from "@/components/ui/switch";
import { Check, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FoundersBanner, founderPrice } from "@/components/FoundersClub";
import { PlanTerms, ForfeitureAcknowledgement } from "@/components/PlanTerms";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [forfeitAck, setForfeitAck] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const isFounder = !!user?.founder;
  const showYearly = yearly;

  const choose = async (plan) => {
    if (!user) { navigate("/login"); return; }
    if (isFounder && !forfeitAck) { toast.error("Please tick the Founders Club acknowledgement first"); return; }
    const lookup = showYearly ? plan.yearly_lookup : plan.monthly_lookup;
    setBusy(plan.id);
    try {
      await startCheckout(lookup);
    } catch {
      toast.error("Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 ${isFounder ? "founders-pricing" : ""}`} data-testid={isFounder ? "pricing-founder" : "pricing-standard"}>
      <div className="text-center max-w-2xl mx-auto mb-10">
        {isFounder ? (
          <>
            <img src="/founders-club-logo.webp" alt="Futuregen Founders Club" className="w-24 h-24 rounded-full mx-auto border-2 border-amber-300 bg-white shadow-md" data-testid="pricing-founder-logo" />
            <span className="overline text-amber-700 mt-4 inline-block">Founders Club · {user.founder.confirmed ? `Member #${user.founder.number}` : "Place reserved"}</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2 mb-4">Your founder pricing</h1>
            <p className="text-muted-foreground text-lg">{user.founder.percent_off}% off every plan for as long as you stay fully subscribed — renewals stay at the same {user.founder.percent_off}% discounted rate. You pay from day one (no free trial on the Club route) with a 12-month minimum term from your first payment; an annual payment satisfies it.</p>
            {!user.founder.confirmed && <p className="text-xs text-amber-800 mt-2" data-testid="pricing-founder-reserved">Your place is reserved and is numbered and counted from your first payment.</p>}
          </>
        ) : (
          <>
            <span className="overline text-accent">Subscriptions</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2 mb-4">
              Simple plans for every service
            </h1>
            <p className="text-muted-foreground text-lg">
              Ongoing compliance support, AI guidance and member discounts on resources.
            </p>
          </>
        )}
        <div className="inline-flex items-center gap-3 mt-6 bg-secondary rounded-full px-4 py-2">
          <span className={`text-sm font-medium ${!yearly ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
          <Switch checked={yearly} onCheckedChange={setYearly} data-testid="billing-toggle" />
          <span className={`text-sm font-medium ${yearly ? "text-foreground" : "text-muted-foreground"}`}>
            Yearly <span className="text-accent font-semibold">{isFounder ? "(satisfies the 12-month term)" : "(2 months free)"}</span>
          </span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto mb-8"><FoundersBanner speciality={user?.speciality} /></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch max-w-4xl mx-auto">
        {plans.map((plan) => {
          const listPrice = showYearly ? plan.yearly_price : plan.monthly_price;
          const fp = founderPrice(listPrice, user);
          const price = fp ?? listPrice;
          const isCurrent = user?.subscription_tier === plan.id && user?.subscription_status === "active";
          return (
            <div key={plan.id} data-testid={`plan-card-${plan.id}`}
              className={`relative rounded-2xl border p-8 flex flex-col ${isFounder ? "border-amber-300 bg-amber-50/40 shadow-md" :
                plan.highlight ? "border-primary shadow-lg ring-1 ring-primary/20 bg-card" : "border-border bg-card shadow-sm"}`}>
              {plan.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">
                  <Star className="w-3 h-3" /> Most popular
                </span>
              )}
              <h3 className="font-display text-xl font-bold">{plan.name}</h3>
              <p className="text-sm text-muted-foreground mt-1 mb-5">{plan.tagline}</p>
              <div className="flex items-end gap-2 mb-6 min-h-[44px] flex-wrap">
                <Price value={price} suffix={`/${showYearly ? "year" : "month"}`} className="font-display text-4xl font-extrabold tracking-tight" testId={`plan-price-${plan.id}`} />
                <span className="text-xs text-muted-foreground mb-1.5" data-testid={`plan-vat-${plan.id}`}>no VAT added</span>
                {fp != null && <span className="text-muted-foreground line-through text-lg mb-1" data-testid={`plan-list-price-${plan.id}`}>£{listPrice}</span>}
                {fp != null && <span className="mb-1.5 text-[11px] font-bold rounded-full bg-amber-100 text-amber-900 px-2 py-0.5" data-testid={`plan-founder-tag-${plan.id}`}>Founder price · {user.founder.percent_off}% off while fully subscribed</span>}
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <WebOnlyPurchase>
              <Button
                onClick={() => choose(plan)}
                disabled={busy === plan.id || isCurrent || (isFounder && !forfeitAck)}
                variant={plan.highlight ? "default" : "outline"}
                className="w-full"
                data-testid={`subscription-tier-${plan.id}-button`}
              >
                {busy === plan.id ? <Loader2 className="w-4 h-4 animate-spin" />
                  : !user ? "Sign in to choose a plan" : isCurrent ? "Current plan" : user?.founder ? "Join at founder price" : user?.trial_eligible !== false ? "Start 14-day free trial" : "Choose plan"}
              </Button>
              {user && !isCurrent && user?.trial_eligible !== false && (
                <p className="text-[11px] text-muted-foreground text-center mt-2" data-testid={`trial-note-${plan.id}`}>
                  Card taken now, nothing charged for 14 days. Cancel anytime before then and pay nothing. After the trial, a 3-month minimum paid term applies — if you cancel within it, the term remains payable. {showYearly ? "Annual payment satisfies the minimum term." : ""}
                </p>
              )}
              {user && !isCurrent && isFounder && (
                <p className="text-[11px] text-center mt-2" data-testid={`founder-term-note-${plan.id}`}>
                  <span className="text-muted-foreground">You pay from day one at your founder price — no free trial. 12-month minimum term from your first payment{showYearly ? " (this annual payment satisfies it)" : ""}. </span>
                  <span className="text-rose-700 font-semibold">Cancelling inside the 12-month term forfeits your {user.founder.percent_off}% discount and your Founders Club place.</span>
                  <span className="text-muted-foreground"> After 12 months the discount continues at the same rate for as long as you stay fully subscribed. Auto-renews {showYearly ? "annually" : "monthly"} until cancelled.</span>
                </p>
              )}
              {user && !isCurrent && !isFounder && (
                <p className="text-[11px] text-muted-foreground text-center mt-1" data-testid={`renewal-note-${plan.id}`}>Auto-renews {showYearly ? "annually" : "monthly"} at the same price until cancelled — cancel in two clicks from Billing.</p>
              )}
              {user && !isCurrent && (!isFounder || forfeitAck) && <div className="text-center mt-2"><PayByInvoiceLink lookupKey={showYearly ? plan.yearly_lookup : plan.monthly_lookup} title={`${plan.name} (${showYearly ? "yearly" : "monthly"})${isFounder ? " — Founders Club 50% off" : ""}`} amount={price} /></div>}
              </WebOnlyPurchase>
            </div>
          );
        })}
      </div>
      <div className="max-w-4xl mx-auto mt-8 space-y-4">
        {user && isFounder && user.subscription_status !== "active" && <ForfeitureAcknowledgement checked={forfeitAck} onChange={setForfeitAck} />}
        <PlanTerms founder={isFounder} />
      </div>
      {user && user.subscription_status !== "active" && <div className="max-w-xl mx-auto mt-10"><RedeemAccessCode /></div>}
    </div>
  );
}
